import React, { useState, useEffect, useRef } from "react";
import CommonValues from "../Common/utils";
import { useNavigate } from "react-router-dom";
import Loader from "../Common/Loader";
import axios from "axios";

interface HistoryLandingPageProps {
  searchTerm: string;
  selectedType: string;
}

export default function HistoryLandingPage({
  searchTerm,
  selectedType,
}: HistoryLandingPageProps) {
  const navigate = useNavigate();
  const toast: any = useRef("");
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistoryItems = async () => {
      setLoading(true);
      try {
        const token = CommonValues.GetToken();
        const { data } = await axios.get(
          `${process.env.REACT_APP_Alexandria_API}/api/Alexandria/alexandria-recentchats`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setHistoryItems(data);
      } catch (error: any) {
        if (error.response?.status === 401) {
          navigate("/");
          CommonValues.Logout(navigate);
        } else {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "Unknown error while loading the details",
            life: 3000,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHistoryItems();
  }, [navigate]);

  const filteredItems = historyItems.filter(
    (item) =>
      item.category?.toLowerCase() === selectedType.toLowerCase() &&
      (item.title || item.question || item.callTitle)
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const groupedByDate: { [key: string]: any[] } = {};
  filteredItems.forEach((item) => {
    const dateKey = new Date(item.chatTime || item.callTime).toLocaleDateString(
      [],
      { day: "2-digit", month: "short", year: "numeric" }
    );
    if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
    groupedByDate[dateKey].push(item);
  });

  return (
    <div className="d-flex flex-column w-100">
      {loading ? (
        <div className="d-flex justify-content-center align-items-center w-100">
          <Loader />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-muted text-center mt-5 small text-purple">
          <i
            className="pi pi-info-circle text-purple"
            style={{ fontSize: "1.2rem" }}
          ></i>
          <div className="text-purple">No {selectedType} history found.</div>
        </div>
      ) : (
        Object.entries(groupedByDate).map(([date, chats]) => (
          <div key={date}>
            <div className="text-muted fw-bold small mb-2 mt-3">{date}</div>
            {chats.map((chat) => (
              <div
                key={chat.conversationId || chat.transactionId || chat.id}
                onClick={() => {
                  navigate(
                    `/chat/${chat.conversationId || chat.transactionId}`,
                    {
                      state: {
                        fromHistory: true,
                        category: chat.category?.toLowerCase() || "support",
                      },
                    }
                  );
                }}
                className="history-card text-14 clickable shadow-sm rounded p-2 mb-4 bg-white"
              >
                <div
                  className="fw-sm text-muted text-truncate small"
                  title={chat.title || chat.question || chat.callTitle}
                >
                  {(chat.title?.trim() || chat.question || chat.callTitle)
                    ?.length > 36
                    ? (
                        chat.title?.trim() ||
                        chat.question ||
                        chat.callTitle
                      ).substring(0, 36) + "..."
                    : chat.title?.trim() || chat.question || chat.callTitle}
                </div>
                <div className="text-muted text-10 mt-1">
                  {new Date(chat.chatTime || chat.callTime).toLocaleTimeString(
                    [],
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
